import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import logger from "../utils/logger";

/**
 * UI Store - Centralized UI state management
 *
 * Manages:
 * - Modal states (delete credential, device claiming, bulk status, password, etc.)
 * - Alert states (forced entry, propped door, event denied)
 * - Loading states (refreshing, syncing, saving, downloading)
 * - Collapse/expand states (device state, credential grid, last action, device actions)
 * - Display preferences (theme, logo, device view mode, action history limit)
 * - Notification system
 *
 * Persistence:
 * - Display preferences (theme, activeLogo, deviceViewMode) persist via middleware
 * - All other state is transient (modals, alerts, loading states)
 *
 * @typedef {Object} UIState
 */

const useUIStore = create(
  devtools(
    persist(
      (set, get) => ({
        // --- Modal States ---
        deleteCredentialModal: {
          isOpen: false,
          credentialId: null,
          patronName: null,
          isDeleting: false,
        },
        isDeviceClaimingModalOpen: false,
        isBulkStatusModalOpen: false,
        showPasswordModal: false,
        isAdvancedSettingsOpen: false,
        isInstallationActionsOpen: false,
        isInstallationManagementOpen: false,
        isOrganizationManagementOpen: false,
        managedOrganizationId: null, // Track which org's installations to manage

        // Device Update Modal - shown when firmware/WiFi changes are detected
        // phase: 'waiting' (for deviceStateUpdate), 'ready' (propagated, auto-refreshing), 'refreshing', 'complete'
        deviceUpdateModal: {
          isOpen: false,
          type: null, // 'firmware' or 'wifi'
          deviceId: null,
          phase: null,
        },

        // --- Alert States ---
        forcedEntryAlert: null,
        proppedDoorAlert: null,
        eventDeniedAlert: null,

        // --- Loading States ---
        isLoading: true,
        isRefreshing: false,
        isSyncing: false,
        isSavingDisplayName: false,
        isDownloadingSettings: false,
        loadingAction: null, // Specific action being loaded

        // --- Collapse States ---
        isDeviceStateCollapsed: false,
        isCredentialGridCollapsed: false,
        isLastActionCollapsed: false,
        isDeviceActionsCollapsed: false,
        isDeviceSettingsCollapsed: false,

        // --- Display Preferences (persisted via middleware) ---
        theme: "dark-mode",
        activeLogo: "schlage",
        deviceViewMode: "detailed",
        actionHistoryLimit: 5, // Number of actions to show in history

        // --- Editing States ---
        isEditingDisplayName: false,
        editingDisplayNameValue: "",

        // --- Notification System ---
        notification: null, // { message, type: 'success' | 'error' | 'info' }

        // --- Last Event Tracking ---
        lastCredentialDownload: null,
        lastCredentialProvisioned: null,
        lastCredentialRemoved: null,

        // --- Bulk Action Tracking ---
        bulkActionResults: [],
        bulkActionStatuses: {},
        bulkActionTimestamps: {}, // Map of actionId -> { createdAt, cloudCreatedAt, cloudUpdatedAt }
        bulkActionType: null,

        // --- Modal Actions ---
        openDeleteCredentialModal: (credentialId, patronName) => {
          logger.log("[UIStore] Opening delete credential modal", {
            credentialId,
            patronName,
          });
          set({
            deleteCredentialModal: {
              isOpen: true,
              credentialId,
              patronName,
              isDeleting: false,
            },
          });
        },

        closeDeleteCredentialModal: () => {
          logger.log("[UIStore] Closing delete credential modal");
          set({
            deleteCredentialModal: {
              isOpen: false,
              credentialId: null,
              patronName: null,
              isDeleting: false,
            },
          });
        },

        setDeleteCredentialModalDeleting: (isDeleting) => {
          set((state) => ({
            deleteCredentialModal: {
              ...state.deleteCredentialModal,
              isDeleting,
            },
          }));
        },

        setDeviceClaimingModalOpen: (isOpen) => {
          logger.log("[UIStore] Device claiming modal", { isOpen });
          set({ isDeviceClaimingModalOpen: isOpen });
        },

        setBulkStatusModalOpen: (isOpen) => {
          logger.log("[UIStore] Bulk status modal", { isOpen });
          set({ isBulkStatusModalOpen: isOpen });
        },

        setPasswordModalOpen: (isOpen) => {
          logger.log("[UIStore] Password modal", { isOpen });
          set({ showPasswordModal: isOpen });
        },

        setAdvancedSettingsOpen: (isOpen) => {
          logger.log("[UIStore] Advanced settings", { isOpen });
          set({ isAdvancedSettingsOpen: isOpen });
        },

        setInstallationActionsOpen: (isOpen) => {
          logger.log("[UIStore] Installation actions", { isOpen });
          set({ isInstallationActionsOpen: isOpen });
        },

        setInstallationManagementOpen: (isOpen, organizationId = null) => {
          logger.log("[UIStore] Installation management", {
            isOpen,
            organizationId,
          });
          set({
            isInstallationManagementOpen: isOpen,
            managedOrganizationId: organizationId,
          });
        },

        setOrganizationManagementOpen: (isOpen) => {
          logger.log("[UIStore] Organization management", { isOpen });
          set({ isOrganizationManagementOpen: isOpen });
        },

        openDeviceUpdateModal: (type, deviceId) => {
          logger.log("[UIStore] Opening device update modal", {
            type,
            deviceId,
          });
          set({
            deviceUpdateModal: {
              isOpen: true,
              type,
              deviceId,
              phase: "waiting",
            },
          });
        },

        markDeviceUpdateReady: (deviceId) => {
          const current = get().deviceUpdateModal;
          if (
            current.isOpen &&
            current.deviceId === deviceId &&
            current.phase === "waiting"
          ) {
            logger.log(
              "[UIStore] Device update ready (ONLINE connectivity received)",
              { deviceId },
            );
            set({
              deviceUpdateModal: { ...current, phase: "ready" },
            });
          }
        },

        setDeviceUpdatePhase: (phase) => {
          const current = get().deviceUpdateModal;
          logger.log("[UIStore] Device update phase change", {
            from: current.phase,
            to: phase,
          });
          set({
            deviceUpdateModal: { ...current, phase },
          });
        },

        closeDeviceUpdateModal: () => {
          logger.log("[UIStore] Closing device update modal");
          set({
            deviceUpdateModal: {
              isOpen: false,
              type: null,
              deviceId: null,
              phase: null,
            },
          });
        },

        // --- Alert Actions ---
        setForcedEntryAlert: (alert) => {
          logger.log("[UIStore] Forced entry alert", { alert });
          set({ forcedEntryAlert: alert });
        },

        clearForcedEntryAlert: () => {
          logger.log("[UIStore] Clearing forced entry alert");
          set({ forcedEntryAlert: null });
        },

        setProppedDoorAlert: (alert) => {
          logger.log("[UIStore] Propped door alert", { alert });
          set({ proppedDoorAlert: alert });
        },

        clearProppedDoorAlert: () => {
          logger.log("[UIStore] Clearing propped door alert");
          set({ proppedDoorAlert: null });
        },

        setEventDeniedAlert: (alert) => {
          logger.log("[UIStore] Event denied alert", { alert });
          set({ eventDeniedAlert: alert });
        },

        clearEventDeniedAlert: () => {
          logger.log("[UIStore] Clearing event denied alert");
          set({ eventDeniedAlert: null });
        },

        clearAllAlerts: () => {
          logger.log("[UIStore] Clearing all alerts");
          set({
            forcedEntryAlert: null,
            proppedDoorAlert: null,
            eventDeniedAlert: null,
          });
        },

        // --- Loading Actions ---
        setLoading: (isLoading) => {
          set({ isLoading });
        },

        setRefreshing: (isRefreshing) => {
          set({ isRefreshing });
        },

        setSyncing: (isSyncing) => {
          set({ isSyncing });
        },

        setSavingDisplayName: (isSaving) => {
          set({ isSavingDisplayName: isSaving });
        },

        setDownloadingSettings: (isDownloading) => {
          set({ isDownloadingSettings: isDownloading });
        },

        setLoadingAction: (action) => {
          set({ loadingAction: action });
        },

        // --- Collapse Actions ---
        toggleDeviceStateCollapsed: () => {
          set((state) => ({
            isDeviceStateCollapsed: !state.isDeviceStateCollapsed,
          }));
        },

        toggleCredentialGridCollapsed: () => {
          set((state) => ({
            isCredentialGridCollapsed: !state.isCredentialGridCollapsed,
          }));
        },

        toggleLastActionCollapsed: () => {
          set((state) => ({
            isLastActionCollapsed: !state.isLastActionCollapsed,
          }));
        },

        toggleDeviceActionsCollapsed: () => {
          set((state) => ({
            isDeviceActionsCollapsed: !state.isDeviceActionsCollapsed,
          }));
        },

        toggleDeviceSettingsCollapsed: () => {
          set((state) => ({
            isDeviceSettingsCollapsed: !state.isDeviceSettingsCollapsed,
          }));
        },

        setDeviceSettingsCollapsed: (collapsed) => {
          set({ isDeviceSettingsCollapsed: collapsed });
        },

        setDeviceStateCollapsed: (collapsed) => {
          set({ isDeviceStateCollapsed: collapsed });
        },

        setCredentialGridCollapsed: (collapsed) => {
          set({ isCredentialGridCollapsed: collapsed });
        },

        setLastActionCollapsed: (collapsed) => {
          set({ isLastActionCollapsed: collapsed });
        },

        setDeviceActionsCollapsed: (collapsed) => {
          set({ isDeviceActionsCollapsed: collapsed });
        },

        // --- Display Preference Actions (auto-persisted via middleware) ---
        setTheme: (theme) => {
          logger.log("[UIStore] Setting theme", { theme });
          set({ theme });
        },

        setActiveLogo: (logo) => {
          logger.log("[UIStore] Setting active logo", { logo });
          set({ activeLogo: logo });
        },

        setDeviceViewMode: (mode) => {
          logger.log("[UIStore] Setting device view mode", { mode });
          set({ deviceViewMode: mode });
        },

        setActionHistoryLimit: (limit) => {
          logger.log("[UIStore] Setting action history limit", { limit });
          set({ actionHistoryLimit: limit });
        },

        // --- Editing Actions ---
        startEditingDisplayName: (currentValue = "") => {
          logger.log("[UIStore] Start editing display name", { currentValue });
          set({
            isEditingDisplayName: true,
            editingDisplayNameValue: currentValue,
          });
        },

        stopEditingDisplayName: () => {
          logger.log("[UIStore] Stop editing display name");
          set({
            isEditingDisplayName: false,
            editingDisplayNameValue: "",
          });
        },

        setEditingDisplayNameValue: (value) => {
          set({ editingDisplayNameValue: value });
        },

        // --- Notification Actions ---
        showNotification: (message, type = "info") => {
          logger.log("[UIStore] Showing notification", { message, type });
          set({ notification: { message, type, timestamp: Date.now() } });
        },

        showSuccessNotification: (message) => {
          get().showNotification(message, "success");
        },

        showErrorNotification: (message) => {
          get().showNotification(message, "error");
        },

        clearNotification: () => {
          set({ notification: null });
        },

        // --- Last Event Tracking Actions ---
        setLastCredentialDownload: (event) => {
          set({ lastCredentialDownload: event });
        },

        setLastCredentialProvisioned: (event) => {
          set({ lastCredentialProvisioned: event });
        },

        setLastCredentialRemoved: (event) => {
          set({ lastCredentialRemoved: event });
        },

        // --- Bulk Action Actions ---
        setBulkActionResults: (results) => {
          set({ bulkActionResults: results });
        },

        addBulkActionResult: (result) => {
          set((state) => ({
            bulkActionResults: [...state.bulkActionResults, result],
          }));
        },

        clearBulkActionResults: () => {
          set({ bulkActionResults: [] });
        },

        setBulkActionStatuses: (statusesOrUpdater) => {
          set((state) => {
            // Support callback pattern: setBulkActionStatuses((prev) => newStatuses)
            const newStatuses =
              typeof statusesOrUpdater === "function"
                ? statusesOrUpdater(state.bulkActionStatuses)
                : statusesOrUpdater;
            return { bulkActionStatuses: newStatuses };
          });
        },

        updateBulkActionStatus: (deviceId, status) => {
          set((state) => ({
            bulkActionStatuses: {
              ...state.bulkActionStatuses,
              [deviceId]: status,
            },
          }));
        },

        clearBulkActionStatuses: () => {
          set({ bulkActionStatuses: {} });
        },

        setBulkActionTimestamps: (timestampsOrUpdater) => {
          set((state) => {
            const newTimestamps =
              typeof timestampsOrUpdater === "function"
                ? timestampsOrUpdater(state.bulkActionTimestamps)
                : timestampsOrUpdater;
            return { bulkActionTimestamps: newTimestamps };
          });
        },

        updateBulkActionTimestamp: (actionId, timestamps) => {
          set((state) => ({
            bulkActionTimestamps: {
              ...state.bulkActionTimestamps,
              [actionId]: timestamps,
            },
          }));
        },

        clearBulkActionTimestamps: () => {
          set({ bulkActionTimestamps: {} });
        },

        setBulkActionType: (type) => {
          set({ bulkActionType: type });
        },

        // --- Reset Actions ---
        resetModals: () => {
          logger.log("[UIStore] Resetting all modals");
          set({
            deleteCredentialModal: {
              isOpen: false,
              credentialId: null,
              patronName: null,
              isDeleting: false,
            },
            isDeviceClaimingModalOpen: false,
            isBulkStatusModalOpen: false,
            showPasswordModal: false,
            isAdvancedSettingsOpen: false,
            isInstallationActionsOpen: false,
            isInstallationManagementOpen: false,
            isOrganizationManagementOpen: false,
            managedOrganizationId: null,
          });
        },

        resetLoadingStates: () => {
          logger.log("[UIStore] Resetting all loading states");
          set({
            isLoading: false,
            isRefreshing: false,
            isSyncing: false,
            isSavingDisplayName: false,
            isDownloadingSettings: false,
            loadingAction: null,
          });
        },

        reset: () => {
          logger.log("[UIStore] Resetting all UI state");
          set({
            // Modals
            deleteCredentialModal: {
              isOpen: false,
              credentialId: null,
              patronName: null,
              isDeleting: false,
            },
            isDeviceClaimingModalOpen: false,
            isBulkStatusModalOpen: false,
            showPasswordModal: false,
            isAdvancedSettingsOpen: false,
            isInstallationActionsOpen: false,
            isInstallationManagementOpen: false,
            isOrganizationManagementOpen: false,
            managedOrganizationId: null,
            // Alerts
            forcedEntryAlert: null,
            proppedDoorAlert: null,
            eventDeniedAlert: null,
            // Loading
            isLoading: true,
            isRefreshing: false,
            isSyncing: false,
            isSavingDisplayName: false,
            isDownloadingSettings: false,
            loadingAction: null,
            // Collapse
            isDeviceStateCollapsed: false,
            isCredentialGridCollapsed: false,
            isLastActionCollapsed: false,
            isDeviceActionsCollapsed: false,
            isDeviceSettingsCollapsed: false,
            // Editing
            isEditingDisplayName: false,
            editingDisplayNameValue: "",
            // Notification
            notification: null,
            // Events
            lastCredentialDownload: null,
            lastCredentialProvisioned: null,
            lastCredentialRemoved: null,
            // Bulk actions
            bulkActionResults: [],
            bulkActionStatuses: {},
            bulkActionType: null,
            // Device Update Modal
            deviceUpdateModal: {
              isOpen: false,
              type: null,
              deviceId: null,
              phase: null,
            },
            // Note: Theme, logo, deviceViewMode, and actionHistoryLimit are NOT reset
            // as they are user preferences persisted via middleware
          });
        },
      }),
      {
        name: "ui-preferences",
        partialize: (state) => ({
          theme: state.theme,
          activeLogo: state.activeLogo,
          deviceViewMode: state.deviceViewMode,
        }),
      },
    ),
    { name: "UIStore" },
  ),
);

export default useUIStore;
